import { makeAuthenticatedServiceRequest } from './servicesClient';
import { FetchResponse } from 'src/types/fetch';
import {
    SurfaceAreaType,
    SurfaceFoldLineType,
    SurfaceGroupType,
    SurfaceResponseAdditionalDataType,
    SurfaceResponseType,
} from 'src/types/surface';
import { resolveEffectiveSku } from 'src/util/skuKey';

const ENDPOINT = 'https://cdn-surface-explorer.products.cimpress.io';

// TODO: revisit this function cause it is ugly
function parseClipArea(docAdditionalData: SurfaceResponseAdditionalDataType, type: string) {
    if (!docAdditionalData) {
        return;
    }

    if (!docAdditionalData.masks || docAdditionalData.masks.length === 0) {
        return;
    }

    for (let i = 0; i < docAdditionalData.masks.length; i++) {
        const maskElement = docAdditionalData.masks[i];

        if (!maskElement.pathType || maskElement.pathType !== type) {
            continue; // eslint-disable-line no-continue
        }

        if (!maskElement.paths || maskElement.paths.length === 0) {
            continue; // eslint-disable-line no-continue
        }

        const maskPaths: SurfaceAreaType[] = [];
        maskElement.paths.forEach((maskPath) => {
            if (!maskPath.pathPoints || maskPath.pathPoints.length !== 3) {
                if (maskElement.boundingArea && maskElement.boundingArea.type === 'Rectangle') {
                    maskPaths.push({
                        x: maskElement.boundingArea.position.x,
                        y: maskElement.boundingArea.position.y,
                        width: maskElement.boundingArea.position.width,
                        height: maskElement.boundingArea.position.height,
                        pathPoints: maskPath.pathPoints,
                        anchorX: maskPath.anchorX, // We need this value but unknown why is being replace for the boundingArea :\
                        anchorY: maskPath.anchorY,
                    });
                }
                return;
            }

            maskPaths.push({
                x: maskPath.anchorX,
                y: maskPath.anchorY,
                anchorX: maskPath.anchorX,
                anchorY: maskPath.anchorY,
                width: maskPath.pathPoints[1].endPointX - maskPath.anchorX,
                height: maskPath.pathPoints[1].endPointY - maskPath.anchorY,
                pathPoints: maskPath.pathPoints,
            });
        });

        // eslint-disable-next-line consistent-return
        return maskPaths;
    }
}

function parseFoldLines(docAdditionalData: SurfaceResponseAdditionalDataType) {
    if (!docAdditionalData || !docAdditionalData.guides || docAdditionalData.guides.length === 0) {
        return;
    }
    const fold: SurfaceFoldLineType[] = [];
    for (let i = 0; i < docAdditionalData.guides.length; i++) {
        const guideElement = docAdditionalData.guides[i];

        if (guideElement.pathType === 'FOLD' && guideElement.paths && guideElement.paths.length !== 0) {
            guideElement.paths.forEach((path) => {
                // straight fold lines
                if (
                    path.pathPoints.length === 1 &&
                    !path.pathPoints[0].firstControlPointX &&
                    !path.pathPoints[0].secondControlPointX
                ) {
                    fold.push({
                        startX: path.anchorX,
                        startY: path.anchorY,
                        endX: path.pathPoints[0].endPointX,
                        endY: path.pathPoints[0].endPointY,
                    });
                }
            });
        }
    }

    return fold; // eslint-disable-line consistent-return
}

function mapSurface(surface: SurfaceResponseType) {
    const fullBleedAreas = parseClipArea(surface.docAdditionalData, 'BLEED') || [];
    const trimAreas = parseClipArea(surface.docAdditionalData, 'TRIM') || [];
    let fullBleedArea = {};

    if (!fullBleedAreas || fullBleedAreas.length === 0) {
        fullBleedArea = {
            x: 0,
            y: 0,
            width: surface.widthCm,
            height: surface.heightCm,
        };
    } else {
        fullBleedArea = fullBleedAreas[0];
    }

    return {
        id: surface.id,
        name: surface.location?.name || surface.name,
        technology: surface.processType,
        trimArea: trimAreas.length > 0 ? trimAreas[0] : {}, // Principal TrimArea use as default
        fullBleedArea, // Principal FullBleedArea us as default
        foldLines: parseFoldLines(surface.docAdditionalData),
        width: surface.widthCm,
        height: surface.heightCm,
        trimAreas, // All trim areas use for automasking
        fullBleedAreas, // All fullBleedAreas use for automasking
    };
}

function mapSurfaceGroup(group: SurfaceGroupType) {
    return {
        id: group.id,
        surfaces: group.surfaces.map(mapSurface),
    };
}

function parseProductResponse(response: FetchResponse | void) {
    // Validate the surface spec contains at least one surface group
    if (response && response.data && response.data.surfaceGroups.length > 0) {
        // Validate each surface group contains non-empty surfaces
        for (let i = 0; i < response.data.surfaceGroups.length; i++) {
            if (response.data.surfaceGroups[i].surfaces.length === 0) {
                throw new Error('An empty surface was found in the surface specification!');
            }
        }

        return response.data.surfaceGroups.map(mapSurfaceGroup);
    } else {
        throw new Error('No surface groups found in the surface specification!');
    }
}

/**
 * Retrieves SKU information from the surface specifications service.
 * @param sku - The product SKU.
 * @param variables - Optional product variables.
 * @returns - A promise of the product information.
 */
export function retrieveSurfaceData(sku: string, skuVersion: number | undefined, variables = {}) {
    if (!sku) {
        return Promise.reject(new Error(`retrieveSurfaceData requires a sku, got: sku=${sku}`));
    }
    const skuSegment = skuVersion === undefined ? sku : `${sku}/${skuVersion}`;
    const url = `${ENDPOINT}/v1/${skuSegment}:calculateSurfaceSet?selections=${encodeURIComponent(JSON.stringify(variables))}`;
    return makeAuthenticatedServiceRequest({ url }).then(parseProductResponse);
}

type RetrieveSurfaceParams = {
    sku: string;
    skuVersion?: number;
    variables?: Record<string, unknown>;
    merchantSku?: string;
    merchantVersion?: number;
    merchantVariables?: Record<string, unknown>;
};

/**
 * Resolves surface data using merchant SKU (PRD) when available, falling back to the fulfiller SKU (CIM).
 * Surface Explorer accepts both PRD and CIM SKUs — PRD returns merchant-configured surfaces.
 */
export function retrieveSurfaceDataForProduct({
    sku,
    skuVersion,
    variables,
    merchantSku,
    merchantVersion,
    merchantVariables,
}: RetrieveSurfaceParams) {
    const { effectiveSku, effectiveVersion } = resolveEffectiveSku({ sku, skuVersion, merchantSku, merchantVersion });
    // Variables are selected to match the resolved SKU: merchant variables accompany a merchant
    // SKU, and fulfiller variables accompany a fulfiller SKU.
    const effectiveVariables = merchantSku ? merchantVariables : variables;
    return retrieveSurfaceData(effectiveSku, effectiveVersion, effectiveVariables);
}

/**
 * Retrieves required variable attributes from the surface specifications service.
 * @param sku - The product SKU (CIM or PRD).
 * @param version - Optional product version.
 * @returns - A promise of the required variable names.
 */
export function retrieveRequiredVariables(sku: string, version: number) {
    const url = `${ENDPOINT}/v1/surfaceVariables/products/${sku}?version=${version}`;
    return makeAuthenticatedServiceRequest({ url }).then(
        (resp) => (resp?.data as string[]) ?? [],
        () => undefined,
    );
}
